import { z } from 'zod';
import { assistantContextRequestSchema, assistantContextResultSchema, } from './assistant-context.contract.js';
import { assistantConversationMessageSchema, assistantConversationSchema, } from './assistant-conversation.contract.js';
const uuid = z.string().uuid();
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const capability = z.string().min(5).max(160)
    .regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/);
const moduleId = z.string().min(2).max(64).regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/);
export const assistantModelProviderHostCapability = 'assistant.model-provider-host.v1';
export const assistantModelProviderSlot = 'assistant.server.model-provider';
export const assistantTurnLifecycleCapability = 'assistant.turn-lifecycle.v1';
export const assistantTurnStatusSchema = z.enum([
    'queued', 'running', 'completed', 'unavailable', 'failed', 'cancelled',
]);
export const assistantTurnFailureCodeSchema = z.enum([
    'provider-not-configured',
    'provider-not-enabled',
    'provider-permission-denied',
    'provider-invalid-response',
    'provider-failed',
    'context-failed',
    'cancelled-by-user',
]);
export const assistantTurnEventTypeSchema = z.enum([
    'queued', 'running', 'context-collected', 'provider-selected', 'delta',
    'completed', 'unavailable', 'failed', 'cancelled', 'retried',
]);
export const assistantTurnFailureSchema = z.strictObject({
    code: assistantTurnFailureCodeSchema,
    message: z.string().trim().min(1).max(500),
    retryable: z.boolean(),
});
export const assistantTurnSchema = z.strictObject({
    id: uuid,
    tenantId: uuid,
    conversationId: uuid,
    userMessageId: uuid,
    assistantMessageId: uuid.nullable(),
    status: assistantTurnStatusSchema,
    attempt: z.number().int().positive(),
    revision: z.number().int().positive(),
    requestHash: sha256,
    preferredProviderCapability: capability.nullable(),
    providerCapability: capability.nullable(),
    providerModuleId: moduleId.nullable(),
    modelId: z.string().trim().min(1).max(160).nullable(),
    finishReason: z.enum(['completed', 'refused', 'content-filtered']).nullable(),
    failure: assistantTurnFailureSchema.nullable(),
    createdByMembershipId: uuid,
    createdAt: z.string().datetime({ offset: true }),
    startedAt: z.string().datetime({ offset: true }).nullable(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    cancelledAt: z.string().datetime({ offset: true }).nullable(),
});
export const assistantTurnEventSchema = z.strictObject({
    id: uuid,
    tenantId: uuid,
    turnId: uuid,
    sequence: z.number().int().positive(),
    eventType: assistantTurnEventTypeSchema,
    status: assistantTurnStatusSchema,
    text: z.string().max(8_000).nullable(),
    providerCapability: capability.nullable(),
    providerModuleId: moduleId.nullable(),
    modelId: z.string().trim().min(1).max(160).nullable(),
    failureCode: assistantTurnFailureCodeSchema.nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
export const assistantTurnContextRequestSchema = assistantContextRequestSchema.omit({
    requestId: true,
});
export const assistantTurnStartSchema = z.strictObject({
    commandId: uuid,
    conversationId: uuid.nullable().default(null),
    title: z.string().trim().min(1).max(160).nullable().default(null),
    message: z.string().trim().min(1).max(50_000),
    preferredProviderCapability: capability.nullable().default(null),
    contextRequests: z.array(assistantTurnContextRequestSchema).max(8).default([]),
}).superRefine((request, context) => {
    if (request.conversationId !== null && request.title !== null) {
        context.addIssue({
            code: 'custom', path: ['title'],
            message: 'A title is accepted only when a new conversation is created.',
        });
    }
    const requestedProviderCount = request.contextRequests.reduce((count, contextRequest) => count + contextRequest.requestedCapabilities.length, 0);
    if (requestedProviderCount > 50) {
        context.addIssue({
            code: 'custom', path: ['contextRequests'],
            message: 'A turn can request at most 50 context-provider results.',
        });
    }
});
export const assistantTurnCancelSchema = z.strictObject({
    commandId: uuid,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
});
export const assistantTurnRetrySchema = z.strictObject({
    commandId: uuid,
    expectedRevision: z.number().int().positive(),
});
export const assistantTurnStartResultSchema = z.strictObject({
    conversation: assistantConversationSchema,
    userMessage: assistantConversationMessageSchema,
    turn: assistantTurnSchema,
    idempotent: z.boolean(),
});
export const assistantTurnMutationResultSchema = z.strictObject({
    turn: assistantTurnSchema,
    idempotent: z.boolean(),
});
export const assistantTurnDetailsSchema = z.strictObject({
    turn: assistantTurnSchema,
    events: z.array(assistantTurnEventSchema).max(2_500),
    assistantMessage: assistantConversationMessageSchema.nullable(),
});
export const assistantConversationListQuerySchema = z.strictObject({
    status: z.enum(['active', 'archived']).default('active'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).max(10_000).default(0),
});
export const assistantConversationListResultSchema = z.strictObject({
    data: z.array(assistantConversationSchema).max(100),
});
export const assistantProviderMessageSchema = z.strictObject({
    role: z.enum(['user', 'assistant']),
    text: z.string().min(1).max(50_000),
});
export const assistantModelProviderRequestSchema = z.strictObject({
    contractVersion: z.literal(assistantModelProviderHostCapability),
    requestId: uuid,
    turnId: uuid,
    conversationId: uuid,
    attempt: z.number().int().positive().max(20),
    messages: z.array(assistantProviderMessageSchema).min(1).max(40),
    contextResults: z.array(assistantContextResultSchema).max(50),
    locale: z.string().min(2).max(35),
    maximumOutputCharacters: z.number().int().min(1).max(50_000),
});
export const assistantModelProviderDeltaSchema = z.strictObject({
    sequence: z.number().int().positive(),
    text: z.string().min(1).max(8_000),
});
export const assistantModelProviderResponseSchema = z.strictObject({
    contractVersion: z.literal(assistantModelProviderHostCapability),
    providerCapability: capability,
    providerModuleId: moduleId,
    modelId: z.string().trim().min(1).max(160),
    finishReason: z.enum(['completed', 'refused', 'content-filtered']),
    deltas: z.array(assistantModelProviderDeltaSchema).max(100),
    content: z.string().min(1).max(50_000),
}).superRefine((response, context) => {
    const sequences = response.deltas.map(({ sequence }) => sequence);
    if (sequences.some((sequence, index) => sequence !== index + 1)) {
        context.addIssue({ code: 'custom', path: ['deltas'], message: 'Provider deltas must be contiguous.' });
    }
    if (response.deltas.length > 0 && response.deltas.map(({ text }) => text).join('') !== response.content) {
        context.addIssue({ code: 'custom', path: ['content'], message: 'Provider content must equal its deltas.' });
    }
});
//# sourceMappingURL=assistant-turn.contract.js.map